import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axios from "axios";
import { ArrowRight, BadgeCheck, Book, BookOpen, Briefcase, Building2, Calendar, ChevronRight, Church, ChevronDown, ClipboardList, Crown, Download, ExternalLink, Flag, Flame, Gamepad2, Gavel, GraduationCap, HardHat, Heart, HeartHandshake, Home, Landmark, Layers, Mail, MapPin, Megaphone, PenTool, PlayCircle, Printer, Radio, Scale, Search, Server, Shield, ShieldCheck, ShoppingCart, Sparkles, Star, Store, TrendingUp, Trophy, UserPlus, Users, Vote, Wallet, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EarlyVoteCountdown } from "../components/EarlyVoteCountdown";
import { Link, useNavigate } from "react-router-dom";
import { JuneteenthSpotlight } from "../components/JuneteenthBanner";
import { MusicButton } from "../components/MusicButton";
import SpinningGlobe from "../components/SpinningGlobe";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";
import { ElectionCountdown } from "../components/ElectionCountdown";
import { FourPillarsStrip } from "../components/FourPillarsStrip";
import { WLOKOnAirCard } from "../components/WLOKOnAirCard";

// ==================== LIVE STATS SECTION ====================
const LiveStatsSection = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/live-stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <section className="py-12 bg-slate-50" data-testid="live-stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-sm font-semibold text-primary tracking-wider uppercase mb-6">Our Platform</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl md:text-5xl font-bold text-primary font-serif mb-2">{stats ? stats.unions_listed : '66'}+</div>
            <p className="text-slate-600">Unions Listed</p>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-secondary font-serif mb-2">{stats ? stats.stewards_trained : '0'}</div>
            <p className="text-slate-600">Stewards Trained</p>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-primary font-serif mb-2">{stats ? stats.certificates_earned : '0'}</div>
            <p className="text-slate-600">Certificates Earned</p>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-secondary font-serif mb-2">{stats ? stats.quizzes_passed : '0'}</div>
            <p className="text-slate-600">Quizzes Passed</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const TodayInHistory = () => {
  const [fact, setFact] = useState(null);
  useEffect(() => {
    axios.get('/api/labor-history/today').then(res => setFact(res.data)).catch(() => {});
  }, []);
  if (!fact) return null;
  return (
    <div className="p-5 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl border border-slate-700 text-white" data-testid="today-in-history">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-14 h-14 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
          <Calendar className="w-6 h-6 text-amber-400" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-1">Today in Labor History — {fact.date}, {fact.year}</p>
          <p className="text-sm text-slate-200 leading-relaxed">{fact.event}</p>
        </div>
      </div>
    </div>
  );
};


// TheSouthHomeBanner removed from homepage (Jul 2026) — The South lives at /the-south + top strip

const HeroGroup = ({ id, label, icon: Icon, color, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={`hero-group-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`hero-group-toggle-${id}`}
        className={`w-full flex items-center gap-1.5 mb-1.5 text-[10px] font-black tracking-[0.3em] uppercase ${color} hover:text-white transition-colors`}
      >
        <Icon className="w-3 h-3 shrink-0" />
        <span>{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 ml-auto shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${open ? "opacity-100" : "max-h-0 opacity-0"}`}
        data-testid={`hero-group-body-${id}`}
      >
        {children}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Seed database on first load
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_1e623117-a9d4-406a-a7ae-30d192116c6d/artifacts/152ihwi5_IMG_0686.jpeg" 
            alt="I AM A MAN - Memphis Sanitation Workers" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          {/* World globe showcase — One World. One Movement. */}
          <div className="hidden lg:flex flex-col items-center absolute right-10 xl:right-20 top-1/2 -translate-y-1/2 z-10">
            <button
              onClick={() => window.dispatchEvent(new Event("open-language-menu"))}
              className="group cursor-pointer"
              aria-label="Choose your language"
              data-testid="hero-globe"
            >
              <span className="globe-hero block group-hover:scale-105 transition-transform">
                <SpinningGlobe size="w-72 h-72 xl:w-96 xl:h-96" alt="LaborEducation.org — One World, One Movement" />
              </span>
            </button>
            <p className="mt-5 text-center text-sm font-black tracking-widest text-white uppercase">One World. One Movement.</p>
            <p className="text-xs text-blue-300 font-bold mt-1">Available in 19 languages — tap the globe</p>
          </div>
          <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold font-serif leading-tight mb-6 animate-fade-in">
                {t('hero.title')} <span className="text-secondary">{t('hero.titleHighlight')}</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-5 animate-fade-in stagger-1">
                {t('hero.subtitle')}
              </p>
              <div className="mb-8 animate-fade-in stagger-1" data-testid="hero-endorsement-seals">
                <p className="text-[10px] font-black tracking-[0.3em] text-amber-300 uppercase mb-2 flex items-center gap-1.5">
                  <BadgeCheck className="w-3 h-3" /> Officially Recommended
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "mscs-board", logo: "/mscs-logo-official.png", alt: "Memphis-Shelby County Schools official logo", line1: "Memphis-Shelby County", line2: "Board of Education" },
                    { id: "shelby-county", logo: "/endorsements/seal-shelby-county.jpg", alt: "Seal of Shelby County, Tennessee", line1: "Shelby County Mayor", line2: "Lee Harris" },
                    { id: "workforce-board", logo: "/endorsements/seal-gmlwdb.jpg", alt: "Greater Memphis Local Workforce Development Board seal", line1: "Greater Memphis Workforce", line2: "Development Board" },
                  ].map((s) => (
                    <Link
                      key={s.id}
                      to="/endorsements"
                      className="group inline-flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-amber-300/40 backdrop-blur-md transition-colors"
                      data-testid={`hero-seal-${s.id}`}
                    >
                      <img
                        src={s.logo}
                        alt={s.alt}
                        className="w-10 h-10 rounded-full bg-white p-0.5 object-contain flex-shrink-0"
                        data-testid={`hero-seal-${s.id}-logo`}
                      />
                      <span className="text-left leading-tight text-[11px] sm:text-xs font-bold text-white">
                        {s.line1}
                        <br />
                        {s.line2}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-amber-300 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
              <div className="space-y-4 animate-fade-in stagger-2">
                <Button asChild className="bg-white text-slate-900 hover:bg-blue-50 border border-blue-200 h-auto min-h-11 px-4 py-3 text-sm font-bold whitespace-normal text-left">
                  <Link to="/explore" data-testid="hero-explore-directory-btn"><Layers className="mr-2 w-4 h-4 shrink-0" /><span>Explore Labor Education</span><ArrowRight className="ml-2 w-4 h-4 shrink-0" /></Link>
                </Button>
                <HeroGroup id="vote" label="Vote 2026" icon={Vote} color="text-red-400" defaultOpen={true}>
                  
                  <ElectionCountdown />
                  <div className="flex flex-wrap gap-1.5">
                <Button
                  className="bg-red-700 hover:bg-red-600 text-white h-8 px-3 text-xs font-bold ring-2 ring-amber-400"
                  onClick={() => navigate('/ballot-2026')}
                  data-testid="hero-ballot-2026-btn"
                >
                  <Vote className="mr-1 w-3 h-3 text-amber-300" />
                  Memphis Referendums — Plain English
                </Button>
                <Button
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/voter-hub')}
                  data-testid="hero-voter-hub-btn"
                >
                  <Vote className="mr-1 w-3 h-3" />
                  Southern Voter Power Hub
                </Button>
                <Button
                  className="bg-blue-800 hover:bg-blue-700 text-white h-8 px-3 text-xs font-bold ring-2 ring-amber-400"
                  onClick={() => navigate('/friends-of-labor')}
                  data-testid="hero-friends-of-labor-btn"
                >
                  <Vote className="mr-1 w-3 h-3 text-amber-300" />
                  Friends of Labor · 2026
                </Button>
                <Button
                  className="bg-red-800 hover:bg-red-700 text-white h-8 px-3 text-xs font-bold ring-2 ring-amber-400"
                  onClick={() => navigate('/clc-endorsements')}
                  data-testid="hero-clc-endorsements-btn"
                >
                  <BadgeCheck className="mr-1 w-3 h-3 text-amber-300" />
                  Official CLC Endorsements
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-500 text-white h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/playbook')}
                  data-testid="hero-playbook-btn"
                >
                  <Megaphone className="mr-1 w-3 h-3" />
                  Civic Power Playbook
                </Button>
                  </div>
                </HeroGroup>
                <HeroGroup id="cost" label="Cost of Living" icon={Wallet} color="text-amber-400" defaultOpen={false}>
                  
                  <div className="flex flex-wrap gap-1.5">
                <Button
                  className="bg-blue-600/30 border border-blue-400/50 hover:bg-blue-600/45 text-blue-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/fair-price')}
                  data-testid="hero-fair-price-btn"
                >
                  <ShieldCheck className="mr-1 w-3 h-3" />
                  Fight Digital Price Discrimination
                </Button>
                <Button
                  className="bg-orange-500/30 border border-orange-400/50 hover:bg-orange-500/45 text-orange-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/end-grocery-tax')}
                  data-testid="hero-grocery-tax-btn"
                >
                  <ShoppingCart className="mr-1 w-3 h-3" />
                  End the Grocery Tax
                </Button>
                <Button
                  className="bg-blue-700 hover:bg-blue-600 text-white h-auto py-1.5 px-3 text-xs font-bold whitespace-normal text-left leading-tight ring-2 ring-amber-400"
                  onClick={() => navigate('/united-way')}
                  data-testid="hero-united-way-btn"
                >
                  <Heart className="mr-1 w-3 h-3 text-amber-300 flex-shrink-0" />
                  Financial Crisis Help · United Way Mid-South
                </Button>
                <Button
                  className="bg-emerald-600/30 border border-emerald-400/50 hover:bg-emerald-600/45 text-emerald-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/iptv')}
                  data-testid="hero-cost-iptv-btn"
                >
                  <ShieldCheck className="mr-1 w-3 h-3" />
                  Cut the Cable Bill — IPTV
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-500 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/youdle')}
                  data-testid="hero-youdle-btn"
                >
                  <img src="/youdle-logo.png" alt="Youdle" className="mr-1.5 w-4 h-4 object-contain rounded-full flex-shrink-0" />
                  Youdle — Find It In Stock
                </Button>
                  </div>
                </HeroGroup>
                <HeroGroup id="learn" label="Learn & Train" icon={GraduationCap} color="text-sky-400" defaultOpen={false}>
                  
                  <div className="flex flex-wrap gap-1.5">
                <Button
                  className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white h-8 px-3 text-xs font-black ring-2 ring-red-300/50"
                  onClick={() => navigate('/d10-class')}
                  data-testid="hero-d10-class-btn"
                >
                  <HardHat className="mr-1 w-3 h-3 text-amber-300" />
                  District 10 Trades Pathways — Free Coursework
                </Button>
                <Button
                  className="bg-slate-800 hover:bg-slate-700 text-white h-8 px-3 text-xs font-semibold border border-amber-400/50"
                  onClick={() => navigate('/d10-school-signup')}
                  data-testid="hero-d10-school-packet-btn"
                >
                  <ClipboardList className="mr-1 w-3 h-3 text-amber-300" />
                  High School Sign-Up Packet
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 h-8 px-3 text-xs"
                  onClick={() => navigate('/learn')}
                  data-testid="hero-learn-btn"
                >
                  {t('hero.learnButton')}
                </Button>
                <Button
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white h-8 px-3 text-xs font-black ring-2 ring-amber-300/60"
                  onClick={() => navigate('/academy')}
                  data-testid="hero-academy-btn"
                >
                  <GraduationCap className="mr-1 w-3 h-3" />
                  Labor Education Academy
                </Button>
                <Button
                  className="bg-white/15 border border-white/25 hover:bg-white/25 text-white h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/mscs')}
                  data-testid="hero-mscs-btn"
                >
                  <img src="/mscs-logo-official.png" alt="" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  Memphis-Shelby County Schools
                </Button>
                <Button
                  className="bg-white/15 border border-white/25 hover:bg-white/25 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/steward-training')}
                  data-testid="hero-steward-training-btn"
                >
                  <Sparkles className="mr-1 w-3 h-3 text-amber-400" />
                  Steward Training
                </Button>
                <Button
                  className="bg-teal-600/25 border border-teal-400/50 hover:bg-teal-600/40 text-teal-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/grievance-writing-school')}
                  data-testid="hero-grievance-writing-btn"
                >
                  <PenTool className="mr-1 w-3 h-3" />
                  Grievance Writing
                </Button>
                <Button
                  className="bg-violet-600/25 border border-violet-400/50 hover:bg-violet-600/40 text-violet-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/organizing-school')}
                  data-testid="hero-organizing-school-btn"
                >
                  <Megaphone className="mr-1 w-3 h-3" />
                  Organizing School
                </Button>
                <Button
                  className="bg-rose-600/25 border border-rose-400/50 hover:bg-rose-600/40 text-rose-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/arbitration-school')}
                  data-testid="hero-arbitration-school-btn"
                >
                  <Gavel className="mr-1 w-3 h-3" />
                  Arbitration Training
                </Button>
                <Button
                  className="bg-amber-500/25 border border-amber-400/50 hover:bg-amber-500/40 text-amber-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/negotiation-school')}
                  data-testid="hero-negotiation-school-btn"
                >
                  <Scale className="mr-1 w-3 h-3" />
                  Negotiation School
                </Button>
                <Button
                  className="bg-[#E79AB4]/25 border border-[#E79AB4]/60 hover:bg-[#E79AB4]/40 text-[#f5c3d3] h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/vonda-school')}
                  data-testid="hero-vonda-school-btn"
                >
                  <Flame className="mr-1 w-3 h-3" />
                  Vonda McDaniel School of Movement Building
                </Button>
                <Button
                  className="bg-emerald-500/25 border border-emerald-400/40 hover:bg-emerald-500/35 text-emerald-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/scholarships')}
                  data-testid="hero-free-college-btn"
                >
                  <GraduationCap className="mr-1 w-3 h-3" />
                  FREE College Tuition
                </Button>
                <Button
                  className="bg-sky-600/30 border border-sky-400/50 hover:bg-sky-600/45 text-sky-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/career-pathways')}
                  data-testid="hero-career-pathways-btn"
                >
                  <HardHat className="mr-1 w-3 h-3" />
                  Career Pathways
                </Button>
                <Button
                  className="bg-blue-700 hover:bg-blue-600 text-white h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/tcat-memphis')}
                  data-testid="hero-tcat-btn"
                >
                  <img src="/tcat-icon.png" alt="TCAT Memphis" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  TCAT Memphis — Career in a Year
                </Button>
                <Button
                  className="bg-red-800 hover:bg-red-700 text-white h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/mvahs')}
                  data-testid="hero-mvahs-btn"
                >
                  <img src="/mvahs-logo.png" alt="Memphis Virtual Adult High School" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  Adult High School Diploma — Free
                </Button>
                <Button
                  className="bg-green-700 hover:bg-green-600 text-white h-8 px-3 text-xs font-bold ring-2 ring-emerald-300/60"
                  onClick={() => navigate('/t4s')}
                  data-testid="hero-t4s-btn"
                >
                  <img src="/t4s-logo.png?v=2" alt="T4S" className="mr-1.5 h-5 w-auto object-contain flex-shrink-0 drop-shadow-[0_0_4px_rgba(255,255,255,0.7)]" />
                  Team4Success Youth Leadership
                </Button>
                <a href="https://developingleadership.com" target="_blank" rel="noopener noreferrer">
                  <Button
                    className="bg-slate-800 hover:bg-slate-700 text-white h-8 px-3 text-xs font-semibold border border-slate-500"
                    data-testid="hero-developing-leadership-btn"
                  >
                    <img src="/developing-leadership-logo.png" alt="Developing Leadership" className="mr-1.5 h-4 w-auto object-contain flex-shrink-0" />
                    Developing Leadership — Coaching
                    <ExternalLink className="ml-1 w-3 h-3" />
                  </Button>
                </a>
                <Button
                  className="bg-purple-800 hover:bg-purple-700 text-white h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/eeog')}
                  data-testid="hero-eeog-btn"
                >
                  <img src="/eeog-logo.png" alt="EEOG" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  EEOG — Students to Leaders
                </Button>
                <Button
                  className="bg-orange-600 hover:bg-orange-500 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/memphis-lift')}
                  data-testid="hero-memphis-lift-btn"
                >
                  <img src="/memphis-lift-logo.png" alt="The Memphis LIFT" className="mr-1.5 h-4 w-auto object-contain bg-white rounded px-0.5 flex-shrink-0" />
                  The Memphis LIFT — Powerful Parents
                </Button>
                <Button
                  className="bg-cyan-700 hover:bg-cyan-600 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/sterling-institute')}
                  data-testid="hero-sterling-institute-btn"
                >
                  <img src="/sterling-institute-logo.png" alt="The Sterling Institute" className="mr-1.5 h-4 w-auto object-contain bg-white rounded px-0.5 flex-shrink-0" />
                  The Sterling Institute — Youth 14–24
                </Button>
                <Button
                  className="bg-sky-700 hover:bg-sky-600 text-white h-8 px-3 text-xs font-bold ring-2 ring-sky-300/60"
                  onClick={() => navigate('/student-hub')}
                  data-testid="hero-student-hub-btn"
                >
                  <GraduationCap className="mr-1 w-3 h-3" />
                  Back to School Student Hub
                </Button>
                <Button
                  className="bg-yellow-600 hover:bg-yellow-500 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/muse-foundation')}
                  data-testid="hero-muse-foundation-btn"
                >
                  <img src="/muse-foundation-logo.png" alt="The Muse Foundation" className="mr-1.5 w-4 h-4 object-contain rounded bg-white flex-shrink-0" />
                  The Muse Foundation — Backpacks & Youth
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-3 text-xs" data-testid="hero-youth-library-btn">
                      <Star className="mr-1 w-3 h-3" />
                      Youth Library
                      <ChevronRight className="ml-0.5 w-2.5 h-2.5 rotate-90" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-52">
                    <DropdownMenuItem onClick={() => navigate('/youth-library')} className="cursor-pointer">
                      <BookOpen className="w-4 h-4 mr-2 text-orange-500" /> Youth Library Home
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { navigate('/youth-library'); setTimeout(() => document.querySelector('[data-testid="tab-game"]')?.click(), 600); }} className="cursor-pointer">
                      <img src="/game-assets/hero.png" alt="" className="w-4 h-4 object-contain mr-2" /> Runner Game
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { navigate('/youth-library'); setTimeout(() => document.querySelector('[data-testid="tab-smash"]')?.click(), 600); }} className="cursor-pointer">
                      <img src="/game-assets/hero-idle.png" alt="" className="w-4 h-4 object-contain mr-2" /> Smash Game
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { navigate('/youth-library'); setTimeout(() => document.querySelector('[data-testid="tab-defender"]')?.click(), 600); }} className="cursor-pointer" data-testid="youth-dropdown-defender">
                      <Gamepad2 className="w-4 h-4 mr-2 text-amber-500" /> Defender of the Working Class
                      <span className="ml-auto text-[9px] bg-amber-400 text-black px-1.5 py-0.5 rounded font-bold">NEW</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { navigate('/youth-library'); setTimeout(() => document.querySelector('[data-testid="tab-organizer"]')?.click(), 600); }} className="cursor-pointer" data-testid="youth-dropdown-organizer">
                      <Gamepad2 className="w-4 h-4 mr-2 text-yellow-400" /> Organizer vs. The Bosses
                      <span className="ml-auto text-[9px] bg-yellow-400 text-black px-1.5 py-0.5 rounded font-bold">NEW</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { navigate('/youth-library'); setTimeout(() => document.querySelector('[data-testid="tab-holdtheline"]')?.click(), 600); }} className="cursor-pointer" data-testid="youth-dropdown-holdtheline">
                      <Gamepad2 className="w-4 h-4 mr-2 text-emerald-500" /> Hold the Line
                      <span className="ml-auto text-[9px] bg-emerald-400 text-black px-1.5 py-0.5 rounded font-bold">NEW</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { navigate('/youth-library'); let n = 0; const iv = setInterval(() => { const el = document.querySelector('[data-testid="tab-picketdefense"]'); if (el) { el.focus(); el.click(); clearInterval(iv); el.scrollIntoView({ block: 'center' }); } if (++n > 40) clearInterval(iv); }, 120); }} className="cursor-pointer" data-testid="youth-dropdown-picketdefense">
                      <Gamepad2 className="w-4 h-4 mr-2 text-sky-500" /> Solidarity Forever
                      <span className="ml-auto text-[9px] bg-sky-400 text-black px-1.5 py-0.5 rounded font-bold">3D</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                  </div>
                </HeroGroup>
                <HeroGroup id="media" label="Read & Watch & Listen" icon={BookOpen} color="text-slate-300" defaultOpen={false}>
                  
                  <div className="flex flex-wrap gap-1.5">
                <Button
                  className="bg-[#0d1a2c] hover:bg-[#16283f] text-white h-8 px-3 text-xs font-bold ring-2 ring-red-500"
                  onClick={() => navigate('/digital-media')}
                  data-testid="hero-digital-media-btn"
                >
                  <BookOpen className="mr-1 w-3 h-3 text-red-400" />
                  Union Digital Publications
                </Button>
                <Button
                  className="bg-slate-900 hover:bg-slate-800 text-amber-300 h-8 px-3 text-xs font-bold ring-2 ring-amber-500"
                  onClick={() => navigate('/legends')}
                  data-testid="hero-legends-btn"
                >
                  <Crown className="mr-1 w-3 h-3" />
                  Legends of the Movement
                </Button>
                <Button
                  className="bg-black hover:bg-neutral-900 text-amber-300 h-8 px-3 text-xs font-bold ring-2 ring-amber-500/70"
                  onClick={() => navigate('/lincoln-alumni')}
                  data-testid="hero-lincoln-alumni-btn"
                >
                  <GraduationCap className="mr-1 w-3 h-3" />
                  Lincoln Alumni Association
                </Button>
                <Button
                  className="bg-[#0b2265] hover:bg-[#143089] text-white border-2 border-[#a71930] h-auto py-1.5 px-3 text-xs font-bold whitespace-normal text-left leading-tight"
                  onClick={() => navigate('/jerry-reese')}
                  data-testid="hero-jerry-reese-btn"
                >
                  <Trophy className="mr-1 w-3 h-3 text-amber-300 flex-shrink-0" />
                  Jerry Reese — A League of His Own
                </Button>
                <Button 
                  variant="outline" 
                  className="border-amber-400 text-amber-300 hover:bg-amber-400/10 h-8 px-3 text-xs"
                  onClick={() => navigate('/book')}
                  data-testid="hero-ebook-btn"
                >
                  <BookOpen className="mr-1 w-3 h-3" />
                  Free eBook
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/labor-library')}
                  data-testid="hero-labor-library-btn"
                >
                  <BookOpen className="mr-1 w-3 h-3" />
                  Labor Library
                </Button>
                <Button
                  className="bg-red-600/30 border border-red-400/50 hover:bg-red-600/45 text-red-100 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/the-south')}
                  data-testid="hero-the-south-btn"
                >
                  <Flag className="mr-1 w-3 h-3" />
                  #TheSouthGotSomethingToSay
                </Button>
                <Button
                  className="bg-sky-500 hover:bg-sky-400 text-slate-950 h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/labor-radio')}
                  data-testid="hero-labor-radio-btn"
                >
                  <Radio className="mr-1 w-3 h-3" />
                  Labor on the Radio — 3 Live Stations
                </Button>
                <Button
                  className="bg-cyan-900 hover:bg-cyan-800 text-cyan-100 border border-cyan-400/40 h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/wlok')}
                  data-testid="hero-wlok-btn"
                >
                  <Radio className="mr-1 w-3 h-3" />
                  WLOK — Legendary Memphis Radio
                </Button>
                <Button
                  className="bg-orange-900 hover:bg-orange-800 text-orange-100 border border-orange-400/40 h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/wojg')}
                  data-testid="hero-wojg-btn"
                >
                  <Radio className="mr-1 w-3 h-3" />
                  Stream Live WOJG 94.7 &mdash; The Word &amp; the Music
                </Button>
                <Button
                  className="bg-rose-800 hover:bg-rose-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/kontji-anthony')}
                  data-testid="hero-kontji-btn"
                >
                  <img src="/kontji-anthony-head.jpg" alt="Kontji Anthony" className="mr-1.5 w-4 h-4 object-cover rounded-full bg-white flex-shrink-0" />
                  Kontji Anthony — Anchor to Founder
                </Button>
                <Button
                  className="bg-amber-800 hover:bg-amber-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/david-porter')}
                  data-testid="hero-porter-btn"
                >
                  <img src="/david-porter-head.jpg" alt="David Porter" className="mr-1.5 w-4 h-4 object-cover rounded-full bg-white flex-shrink-0" />
                  David Porter — The Soul Man of Memphis
                </Button>
                <Button
                  className="bg-fuchsia-900 hover:bg-fuchsia-800 text-fuchsia-100 border border-fuchsia-400/40 h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/angela-burton')}
                  data-testid="hero-angela-btn"
                >
                  <img src="/angela-burton.jpg" alt="Angela Latrice Burton" className="mr-1.5 w-4 h-4 object-cover rounded-full bg-white flex-shrink-0" />
                  Angela Burton — Her Untold Secrets
                </Button>
                    <MusicButton testIdPrefix="hero" />
                  </div>
                </HeroGroup>
                <HeroGroup id="health" label="Health & Wellness" icon={Heart} color="text-rose-400" defaultOpen={false}>
                  
                  <div className="flex flex-wrap gap-1.5">
                <Button
                  className="bg-rose-700 hover:bg-rose-600 text-white h-8 px-3 text-xs font-bold ring-2 ring-rose-300/60"
                  onClick={() => navigate('/safe-haven')}
                  data-testid="hero-safe-haven-btn"
                >
                  <HeartHandshake className="mr-1 w-3 h-3" />
                  Safe Haven · Help for Abused Women, Men & Children
                </Button>
                <Button
                  className="bg-teal-700 hover:bg-teal-600 text-white h-8 px-3 text-xs font-bold ring-2 ring-teal-300/60"
                  onClick={() => navigate('/stand-strong')}
                  data-testid="hero-stand-strong-btn"
                >
                  <Shield className="mr-1 w-3 h-3" />
                  Stand Strong — Bullying & Mental Health Help
                </Button>
                <Button
                  className="bg-fuchsia-700 hover:bg-fuchsia-600 text-white h-auto py-1 px-3 text-xs font-bold whitespace-normal text-left leading-tight ring-2 ring-fuchsia-300/70"
                  onClick={() => navigate('/women-empowerment')}
                  data-testid="hero-women-empowerment-btn"
                >
                  <img src="/bradshaw-reign-emblem-clear-v3.png" alt="Reign by Bradshaw Solutions" className="mr-1.5 w-9 h-9 object-contain drop-shadow-[0_0_6px_rgba(251,191,36,0.6)] flex-shrink-0" />
                  Women Empowerment · No More Suffering in Silence
                </Button>
                <Button
                  className="bg-rose-700 hover:bg-rose-600 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/caregivers')}
                  data-testid="hero-caregivers-btn"
                >
                  <HeartHandshake className="mr-1 w-3 h-3 text-rose-200" />
                  Caregivers — You Are Not Alone
                </Button>
                <Button
                  className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/feed-the-needy')}
                  data-testid="hero-feed-the-needy-btn"
                >
                  <img src="/feed-the-needy-logo.png" alt="Feed The Needy" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  Feed The Needy — Become a Donor
                </Button>
                <Button
                  className="bg-cyan-800 hover:bg-cyan-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/regional-one')}
                  data-testid="hero-regional-one-btn"
                >
                  <img src="/regional-one-icon.png" alt="Regional One Health" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  Regional One Health — Hospital, Patients & Employees
                </Button>
                <a href="https://yahkiawakened.com" target="_blank" rel="noopener noreferrer">
                  <Button
                    className="bg-lime-800 hover:bg-lime-700 text-white h-8 px-3 text-xs font-semibold ring-2 ring-lime-400/50"
                    data-testid="hero-yahki-btn"
                  >
                    <img src="/yahki-logo.png" alt="Yahki Awakened" className="mr-1.5 h-4 w-auto object-contain bg-white rounded px-0.5 flex-shrink-0" />
                    Yahki Awakened — Herbal Wellness
                    <ExternalLink className="ml-1 w-3 h-3" />
                  </Button>
                </a>
                <Button
                  className="bg-orange-800 hover:bg-orange-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/african-art')}
                  data-testid="hero-african-art-btn"
                >
                  <img src="/african-art-icon.png" alt="African Art & Accessories" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  African Art & Accessories — Health is Wealth
                </Button>
                <Button
                  className="bg-yellow-800 hover:bg-yellow-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/ethnik-vibes')}
                  data-testid="hero-ethnik-vibes-btn"
                >
                  <img src="/ethnik-vibes-icon.png" alt="Ethnik Vibes" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  Ethnik Vibes — African Goods Store
                </Button>
                <Button
                  className="bg-sky-800 hover:bg-sky-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/united-benefits')}
                  data-testid="hero-united-benefits-btn"
                >
                  <img src="/united-benefits-icon.png" alt="United Benefits" className="mr-1.5 w-4 h-4 object-contain rounded-full bg-white flex-shrink-0" />
                  United Benefits — Insurance & Medicare
                </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5" data-testid="hero-health-note">Wellness resources are educational — for medical emergencies and diagnoses, see a doctor or call 911.</p>
                </HeroGroup>
                <HeroGroup id="community" label="Community & Solidarity" icon={Users} color="text-emerald-400" defaultOpen={false}>
                  
                  <div className="flex flex-wrap gap-1.5">
                <Button
                  className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/unions?view=partners')}
                  data-testid="hero-community-partners-btn"
                >
                  <HeartHandshake className="mr-1 w-3 h-3" />
                  Community Partners
                </Button>
                <Button
                  className="bg-red-700 hover:bg-red-600 text-white h-8 px-3 text-xs font-bold"
                  onClick={() => navigate('/we-refuse-to-be-divided')}
                  data-testid="hero-we-refuse-btn"
                >
                  <Users className="mr-1 w-3 h-3" />
                  We Refuse to Be Divided
                </Button>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/labor-and-the-pulpit')}
                  data-testid="hero-labor-pulpit-btn"
                >
                  <Church className="mr-1 w-3 h-3" />
                  Labor &amp; Pulpit
                </Button>
                <Button 
                  className="bg-stone-900 hover:bg-stone-800 text-amber-300 border border-amber-400/40 h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/i-am-a-man')}
                  data-testid="hero-civil-rights-btn"
                >
                  <Flag className="mr-1 w-3 h-3" />
                  I AM A MAN
                </Button>
                                <Button
                  className="bg-emerald-800 hover:bg-emerald-700 text-emerald-50 border border-emerald-300/40 h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/building-a-better-jail')}
                  data-testid="hero-better-jail-btn"
                >
                  <Building2 className="mr-1 w-3 h-3 text-emerald-200" />
                  Building a Better Jail
                </Button>
                <Button
                  className="bg-red-700 hover:bg-red-600 text-white h-auto py-1.5 px-3 text-xs font-bold whitespace-normal text-left leading-tight ring-2 ring-red-300/70"
                  onClick={() => document.getElementById('grow-unions')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-grow-unions-btn"
                >
                  <TrendingUp className="mr-1 w-3 h-3 text-red-200 flex-shrink-0" />
                  The $1.7 Billion Question · Why Unions Must Grow
                </Button>
                                                <Button
                  className="bg-indigo-700 hover:bg-indigo-600 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/build-with-us')}
                  data-testid="hero-bradshaw-solutions-btn"
                >
                  <PenTool className="mr-1 w-3 h-3 text-amber-300" />
                  Bradshaw Solutions — Websites &amp; More
                </Button>
                                                                                                                                  </div>
                </HeroGroup>
                <HeroGroup id="tools" label="Everyday Tools" icon={Layers} color="text-slate-400" defaultOpen={false}>
                  
                  <div className="flex flex-wrap gap-1.5">
                <Button 
                  className="bg-secondary hover:bg-secondary/90 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/unions')}
                  data-testid="hero-explore-btn"
                >
                  {t('hero.exploreButton')}
                  <ArrowRight className="ml-1 w-3 h-3" />
                </Button>
                <a href="https://aflcio.org/formaunion/contact" target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white h-8 px-3 text-xs"
                    data-testid="hero-join-union-btn"
                  >
                    <UserPlus className="mr-1 w-3 h-3" />
                    Join a Union
                  </Button>
                </a>
                <Button 
                  className="bg-slate-700 hover:bg-slate-600 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/job-search')}
                  data-testid="hero-job-board-btn"
                >
                  <Briefcase className="mr-1 w-3 h-3" />
                  Job Search
                </Button>
                <Button 
                  className="bg-rose-600 hover:bg-rose-700 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/print-shop')}
                  data-testid="hero-print-shop-btn"
                >
                  <Printer className="mr-1 w-3 h-3" />
                  Union Print &amp; Design
                </Button>
                <Button 
                  className="bg-indigo-700 hover:bg-indigo-600 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/legal-partners')}
                  data-testid="hero-legal-partners-btn"
                >
                  <Scale className="mr-1 w-3 h-3" />
                  Legal Partners
                </Button>
                <Button
                  className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/business-hub')}
                  data-testid="hero-business-hub-btn"
                >
                  <Store className="mr-1 w-3 h-3" />
                  Local Business Hub
                </Button>
                                <Button
                  className="bg-amber-600 hover:bg-amber-500 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/libraries')}
                  data-testid="hero-libraries-btn"
                >
                  <BookOpen className="mr-1 w-3 h-3" />
                  Your Library — Free Programs
                </Button>
                                                <Button
                  className="bg-orange-800 hover:bg-orange-700 text-white h-8 px-3 text-xs font-semibold"
                  onClick={() => navigate('/property-taxes')}
                  data-testid="hero-property-taxes-btn"
                >
                  <Landmark className="mr-1 w-3 h-3" />
                  Check Your Property Taxes
                </Button>
                <Button
                  className="bg-blue-700 hover:bg-blue-600 text-white h-8 px-3 text-xs"
                  onClick={() => { window.location.href = 'sms:211211?&body=BENEFITS'; }}
                  data-testid="hero-benefit-kitchen-btn"
                >
                  <Wallet className="mr-1 w-3 h-3" />
                  Check My Benefits — Text BENEFITS to 211-211
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-500 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/driving-the-dream')}
                  data-testid="hero-dtd-btn"
                >
                  <HeartHandshake className="mr-1 w-3 h-3" />
                  Get Help — Driving The Dream
                </Button>
                <Button 
                  className="bg-sky-600 hover:bg-sky-500 text-white h-8 px-3 text-xs"
                  onClick={() => navigate('/data-centers')}
                  data-testid="hero-data-centers-btn"
                >
                  <Server className="mr-1 w-3 h-3" />
                  Data Centers 101
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 text-xs"
                      data-testid="hero-worker-tools-btn"
                    >
                      <Layers className="mr-1 w-3 h-3" />
                      Worker Tools
                      <svg className="ml-1 w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/union-iq')} className="cursor-pointer font-semibold text-amber-700" data-testid="hero-tool-union-iq">
                      <Zap className="mr-2 w-4 h-4 text-amber-600" />
                      Union IQ Quiz
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/labor-history-101')} className="cursor-pointer" data-testid="hero-tool-labor-history">
                      <BookOpen className="mr-2 w-4 h-4 text-red-700" />
                      Labor History 101
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/academy')} className="cursor-pointer font-semibold" data-testid="hero-tool-academy">
                      <GraduationCap className="mr-2 w-4 h-4 text-amber-600" />
                      Labor Education Academy
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/negotiation-school')} className="cursor-pointer" data-testid="hero-tool-negotiation-school">
                      <Scale className="mr-2 w-4 h-4 text-amber-700" />
                      Negotiation School
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/learn/benefits-calculator')} className="cursor-pointer" data-testid="hero-tool-benefits">
                      <TrendingUp className="mr-2 w-4 h-4 text-green-600" />
                      Benefits Calculator
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/learn/financial-literacy')} className="cursor-pointer" data-testid="hero-tool-financial-literacy">
                      <Wallet className="mr-2 w-4 h-4 text-green-700" />
                      Financial Literacy
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/learn/know-your-rights')} className="cursor-pointer" data-testid="hero-tool-rights">
                      <Shield className="mr-2 w-4 h-4 text-blue-600" />
                      Know Your Rights
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/job-readiness')} className="cursor-pointer" data-testid="hero-tool-job-readiness">
                      <Briefcase className="mr-2 w-4 h-4 text-purple-600" />
                      Job Readiness
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/back-to-work')} className="cursor-pointer font-semibold text-emerald-700" data-testid="hero-tool-back-to-work">
                      <Briefcase className="mr-2 w-4 h-4 text-emerald-600" />
                      Back To Work · Reentry Hub
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/learn/legislative-tracker')} className="cursor-pointer" data-testid="hero-tool-legislative">
                      <Scale className="mr-2 w-4 h-4 text-red-600" />
                      Legislative Tracker
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/sim')} className="cursor-pointer" data-testid="hero-tool-sim">
                      <Building2 className="mr-2 w-4 h-4 text-orange-600" />
                      3D Construction Trades Sim
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/tool-training')} className="cursor-pointer" data-testid="hero-tool-tool-training">
                      <HardHat className="mr-2 w-4 h-4 text-orange-600" />
                      Tool Training
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  className="bg-[#f5c400] hover:bg-[#ffd21f] text-slate-950 h-14 px-4 text-sm font-black"
                  onClick={() => navigate('/safety')}
                  data-testid="hero-safety-report-btn"
                >
                  <img
                    src="/local-252g-hard-hat.png"
                    alt=""
                    className="mr-2 h-11 w-11 object-contain"
                    data-testid="hero-safety-hard-hat"
                  />
                  Nobody Gets Hurt On Our Shift
                </Button>
                  </div>
                </HeroGroup>
              </div>
          </div>
        </div>
      </section>

      {/* Early vote countdown */}
      <div className="bg-slate-950 pt-8 px-4 sm:px-6 lg:px-8 -mb-2">
        <EarlyVoteCountdown ctaTo="/friends-of-labor" />
      </div>

      {/* State picker — TN / MS */}
      <section className="bg-slate-950 pt-6 px-4 sm:px-6 lg:px-8" data-testid="home-state-picker">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-400 mb-3 text-center">Pick your state — Friends of Labor & Voter Hub</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-gradient-to-br from-red-950/80 to-slate-900 border-2 border-amber-500/50 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="home-picker-tn">
              <div className="flex-1">
                <p className="text-white font-black text-lg">Tennessee</p>
                <p className="text-slate-400 text-sm">TN AFL-CIO slate · early vote Oct 14–29 · Nov 3</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/friends-of-labor" data-testid="home-picker-tn-fol" className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-black px-4 py-2 rounded-full transition-colors">
                  Friends of Labor <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/voter-hub" data-testid="home-picker-tn-hub" className="inline-flex items-center gap-1.5 border border-amber-500/60 hover:bg-amber-500/10 text-amber-300 text-sm font-black px-4 py-2 rounded-full transition-colors">
                  Voter Hub
                </Link>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-950 to-slate-900 border-2 border-blue-500/50 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="home-picker-ms">
              <div className="flex-1">
                <p className="text-white font-black text-lg">Mississippi</p>
                <p className="text-slate-400 text-sm">MS AFL-CIO slate · absentee Sept 19–Oct 31 · Nov 3</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/friends-of-labor/ms" data-testid="home-picker-ms-fol" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-black px-4 py-2 rounded-full transition-colors">
                  Friends of Labor <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/voter-hub/ms" data-testid="home-picker-ms-hub" className="inline-flex items-center gap-1.5 border border-blue-400/60 hover:bg-blue-500/10 text-blue-200 text-sm font-black px-4 py-2 rounded-full transition-colors">
                  Voter Power Hub
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Today in Labor History — sits on top of the BCTGM banner */}
      <div className="bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <TodayInHistory />
        </div>
      </div>

      {/* Community Stories — featured row */}
      <section className="bg-slate-950 border-b border-white/10 py-8" data-testid="community-stories-row">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-400">Community Stories</p>
              <h2 className="text-white font-black text-lg md:text-lg mt-1">From music to business to movements — Memphis stories worth knowing</h2>
            </div>
            <Button
              variant="outline"
              className="hidden sm:inline-flex border-white/20 bg-white/5 text-white hover:bg-white/15 h-8 px-3 text-xs font-semibold"
              onClick={() => navigate('/labor-stories')}
              data-testid="stories-row-all-btn"
            >
              All Stories →
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { to: '/david-porter', img: '/porter-story-thumb.jpg', title: 'The Soul Man of Memphis', sub: 'David Porter — Stax legend, still building Memphis', id: 'porter' },
              { to: '/kontji-anthony', img: '/kontji-story-thumb.jpg', title: 'From the Anchor Desk to the Grocery Aisle', sub: 'Kontji Anthony — 17 years on TV, then Youdle', id: 'kontji' },
              { to: '/pain-saved-my-life', img: '/pain-story-thumb.jpg', title: 'Pain Saved My Life', sub: "Kevin Bradshaw's health-is-wealth testimony", id: 'kevin' },
              { to: '/ruth-banks-story', img: '/ruth-banks-story-thumb.jpg', title: 'The Grandmother Who Never Stopped', sub: 'Ruth Rawlings-Banks — 32 years of Feed The Needy', id: 'ruth' },
              { to: '/sarah-carpenter-story', img: '/sarah-carpenter-story-thumb.jpg', title: 'Making the Powerless Parent Powerful', sub: "Sarah Carpenter — The Memphis LIFT", id: 'sarah' },
              { to: '/stevie-moore-story', img: '/stevie-moore-story-thumb.jpg', title: 'He Turned Grief Into a Movement', sub: 'Stevie Moore — F.F.U.N. · Stop the Killing', id: 'stevie' },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(s.to)}
                className="text-left bg-slate-900 border border-white/10 hover:border-amber-400/50 rounded-2xl overflow-hidden transition-colors group"
                data-testid={`stories-row-${s.id}`}
              >
                <img src={s.img} alt={s.title} className="w-full aspect-video object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
                <div className="p-4">
                  <p className="text-white font-black text-sm leading-snug">{s.title}</p>
                  <p className="text-slate-400 text-xs mt-1">{s.sub}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* WLOK — Labor on the Air spotlight */}
      <WLOKOnAirCard testId="home-wlok-onair" />

      {/* The Four Pillars — directly under Today in Labor History */}
      <FourPillarsStrip />

      {/* BCTGM Convention banner removed after event (Jul 2026) */}

      {/* Game sections moved to Youth Library (Jul 2026) — compact static arcade card */}
      <section className="bg-slate-950 border-b border-white/10 py-6" data-testid="game-arcade-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-slate-900 to-slate-950 border border-sky-500/30 rounded-2xl px-5 sm:px-8 py-5">
            <div className="flex items-center gap-4">
              <img src="/game-assets/hero.png" alt="Union Hero" className="w-14 h-14 object-contain drop-shadow-lg" loading="lazy" />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-sky-600 text-white font-black text-[10px] px-2 py-0.5">GAME ARCADE</Badge>
                  <h3 className="text-white font-black text-lg sm:text-xl tracking-tight">Union Game Arcade</h3>
                </div>
                <p className="text-slate-300 text-xs sm:text-sm mt-0.5">
                  6 free labor games — Solidarity Forever (3D), Hold the Line, Defender, Organizer, Runner &amp; Smash — now in the Youth Library.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Link to="/youth-library#picketdefense">
                <Button className="bg-sky-600 hover:bg-sky-500 text-white font-black text-sm px-5 py-2.5 shadow-lg hover:scale-105 transition-transform" data-testid="arcade-play-btn">
                  <Gamepad2 className="w-4 h-4 mr-1.5" /> PLAY NOW
                </Button>
              </Link>
              <Link to="/youth-library#game">
                <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-bold text-xs px-4 py-2.5" data-testid="arcade-all-games-btn">
                  <PlayCircle className="w-4 h-4 mr-1.5" /> All Games
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AI Working For Working People — AFL-CIO 30th Convention resolution */}
      <section className="bg-slate-950 border-y border-yellow-500/30 py-0" data-testid="ai-for-workers-banner">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2">
          <div className="relative min-h-[240px] md:min-h-[320px] bg-slate-900">
            <img
              src="https://customer-assets.emergentagent.com/job_787f84ca-2d17-458c-a9ea-1a3a90b71210/artifacts/jnuj80jf_image.jpeg"
              alt="AI Should Work For Us — AFL-CIO 30th Constitutional Convention, Minneapolis"
              className="absolute inset-0 w-full h-full object-contain object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-950/20" />
          </div>
          <div className="px-6 sm:px-10 py-8 sm:py-12 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="bg-yellow-400 text-black text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider">AFL-CIO · 30th Convention</span>
            </div>
            <h2 className="text-white font-black text-2xl sm:text-3xl tracking-tight">AI Working For Working People</h2>
            <p className="text-slate-300 text-sm sm:text-base mt-3 leading-relaxed max-w-xl">
              At the AFL-CIO's 30th Constitutional Convention in Minneapolis, delegates declared
              <span className="text-yellow-300 font-bold"> "AI Should Work For Us"</span> — passing a resolution that technology must be used to <strong className="text-white">empower workers</strong>, not surveil, exploit, or replace them.
            </p>
            <p className="text-slate-400 text-xs sm:text-sm mt-3 leading-relaxed max-w-xl">
              This app is that resolution in action: AI built <em>by labor, for labor</em> — educating union members and youth, telling our stories, and growing worker power on our own terms.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button
                className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-black h-9 px-4 text-xs rounded-full"
                onClick={() => navigate('/ai-workers-school')}
                data-testid="ai-banner-course-btn"
              >
                <GraduationCap className="mr-1.5 w-3.5 h-3.5" /> NEW: Take the AI &amp; Workers Course
              </Button>
              <Button
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-black h-9 px-4 text-xs rounded-full"
                onClick={() => navigate('/labor-and-ai')}
                data-testid="ai-banner-resolution-btn"
              >
                <BookOpen className="mr-1.5 w-3.5 h-3.5" /> Read the Resolution
              </Button>
              <Button
                className="bg-sky-500 hover:bg-sky-400 text-white font-black h-9 px-4 text-xs rounded-full"
                onClick={() => navigate('/data-centers')}
                data-testid="ai-banner-data-centers-btn"
              >
                <Server className="mr-1.5 w-3.5 h-3.5" /> Data Centers 101 · Regulate, Don't Retreat
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Juneteenth Spotlight (auto-hides after June 22) */}
      <JuneteenthSpotlight />

      {/* The South banner + Four Pillars strip moved off homepage (Jul 2026) — see /the-south and /unions */}

      {/* Official Endorsements — compact strip (full letters live at /endorsements) */}
      <section className="bg-white py-10 border-b border-slate-100" data-testid="endorsements-strip">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold tracking-[0.15em] mb-2">
              <BadgeCheck className="w-4 h-4" /> OFFICIAL ENDORSEMENTS
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-serif text-slate-900">Backed by Government &amp; Civic Leaders</h2>
            <p className="text-slate-600 text-sm mt-1">Officially endorsed across the Greater Memphis region.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-7">
            {[
              { img: "/endorsements/mayor-lee-harris-letter.jpeg", name: "Mayor Lee Harris", org: "Shelby County Government" },
              { img: "/endorsements/mscs-board-letter-1.jpg", name: "Board Member Sable Otey", org: "Memphis-Shelby County Schools" },
              { img: "/endorsements/workforce-board-letter-1.jpg", name: "Melody Freeman, Exec. Director", org: "Greater Memphis Workforce Board" },
            ].map((e, i) => (
              <Link key={i} to="/endorsements" className="group flex flex-col items-center text-center" data-testid={`endorsement-strip-item-${i}`}>
                <div className="w-28 h-36 rounded-lg shadow-md border border-slate-200 overflow-hidden group-hover:shadow-xl transition-shadow mb-2">
                  <img src={e.img} alt={`${e.name} letter of support`} className="w-full h-full object-cover object-top" loading="lazy" />
                </div>
                <p className="text-sm font-bold text-slate-800 leading-tight">{e.name}</p>
                <p className="text-xs text-slate-500 leading-tight">{e.org}</p>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/endorsements" className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg transition-colors" data-testid="endorsements-read-full-btn">
              Read the Full Letters <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Women Empowerment spotlight — Bradshaw Solutions x Reign Pads */}
      <section className="relative py-12 bg-gradient-to-br from-purple-950 via-fuchsia-950 to-purple-900 border-y-2 border-fuchsia-400/30 overflow-hidden" data-testid="women-empowerment-spotlight">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <Link to="/women-empowerment" className="shrink-0 group" data-testid="women-empowerment-spotlight-image-link">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-tr from-fuchsia-500 via-purple-400 to-fuchsia-400 rounded-2xl opacity-40 blur-xl group-hover:opacity-70 transition-opacity" />
                <img
                  src="/women-empowerment-hero.jpg"
                  alt="Women Empowerment — No More Suffering in Silence"
                  className="relative w-64 md:w-80 rounded-2xl shadow-2xl ring-4 ring-fuchsia-400/50 group-hover:ring-fuchsia-300 group-hover:scale-[1.03] transition-all object-cover"
                />
              </div>
            </Link>
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-3 justify-center md:justify-start mb-3 flex-wrap">
                <img src="/bradshaw-reign-emblem-clear-v3.png" alt="Reign by Bradshaw Solutions" className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-[0_0_12px_rgba(251,191,36,0.45)]" />
                <Badge className="bg-fuchsia-500 text-white font-bold text-xs px-3 py-1 hover:bg-fuchsia-400">WOMEN EMPOWERMENT</Badge>
                <Badge className="bg-purple-500/20 text-purple-100 border border-purple-300/40 text-xs px-2 py-1">A Bradshaw Solutions Initiative</Badge>
              </div>
              <h3 className="text-2xl md:text-4xl font-bold font-serif text-white mb-3 leading-tight">
                No More Suffering in Silence
              </h3>
              <p className="text-purple-100/90 text-sm md:text-base leading-relaxed mb-3 max-w-2xl">
                Heavy monthly flow, menstrual cramps, and mood swings shouldn't be pushed through
                quietly — at work or anywhere else. Discover <strong className="text-fuchsia-300">Reign Premium
                Sanitary Napkins</strong>: ultra thin, 8 layers of protection, over 10X more absorbent, with a
                monthly subscription delivered right to your door.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Link to="/women-empowerment">
                  <Button className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-black px-6 py-3 h-auto shadow-xl hover:scale-105 transition-transform w-full sm:w-auto" data-testid="women-empowerment-spotlight-btn">
                    <Crown className="w-4 h-4 mr-2" /> Visit the Women's Hub
                  </Button>
                </Link>
                <a href="https://bradshawsolutions.jewelpads.com/Shop/" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-bold px-5 py-3 h-auto w-full sm:w-auto" data-testid="women-empowerment-spotlight-shop-btn">
                    <ShoppingCart className="w-4 h-4 mr-2" /> Shop Reign Pads <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Women leading right now */}
          <div className="mt-8 pt-6 border-t border-fuchsia-400/20" data-testid="women-leading-now">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-fuchsia-300 mb-3">
              Women Leading Right Now
            </p>
            <Link
              to="/tiffany-gray"
              className="group flex items-center gap-4 rounded-xl border border-fuchsia-400/25 bg-white/[0.07] p-4 transition-colors hover:border-fuchsia-300/60 hover:bg-white/[0.12]"
              data-testid="women-leading-tiffany-gray"
            >
              <img
                src="/tiffany-gray.jpg"
                alt="Tiffany Mishè Gray"
                className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover object-top ring-2 ring-fuchsia-400/50 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg md:text-xl font-bold text-white leading-tight">Tiffany Mishè Gray</p>
                <p className="text-fuchsia-200 text-xs font-bold uppercase tracking-wider mt-0.5">
                  Memphis · Many Hats, One Job
                </p>
                <p className="text-purple-100/85 text-sm leading-snug mt-1.5">
                  Strategist, organizer and arts educator — founder of The Multicultural Progressive South and
                  Speak Poetry Memphis, writing the stories of people rebuilding careers after incarceration.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-fuchsia-300 shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* NJFAN Featured Newsletter Spotlight */}
      <section className="relative py-10 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 border-y-2 border-amber-500/30 overflow-hidden" data-testid="njfan-newsletter-spotlight">
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            {/* Logo / Pic with glow */}
            <a
              href="https://mailchi.mp/njfac.org/standing-together-for-good-jobs?e=4796746625"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 group"
              data-testid="njfan-newsletter-image-link"
            >
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-tr from-amber-400 via-orange-400 to-amber-500 rounded-full opacity-50 blur-xl group-hover:opacity-80 transition-opacity" />
                <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full bg-white p-3 shadow-2xl ring-4 ring-amber-400/60 group-hover:ring-amber-300 group-hover:scale-105 transition-all">
                  <img
                    src="https://customer-assets.emergentagent.com/job_13e4391a-3898-46d5-8331-1b28fb5b216e/artifacts/j57nlcjh_IMG_0918.png"
                    alt="National Jobs for All Network — Standing Together for Good Jobs"
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
              </div>
            </a>
            {/* Text + CTA */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-2 flex-wrap">
                <Badge className="bg-amber-400 text-slate-900 font-bold text-xs px-3 py-1 hover:bg-amber-300">FEATURED NEWSLETTER</Badge>
                <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/50 text-xs px-2 py-1 font-semibold">★ Labor Education Recognized</Badge>
                <Badge className="bg-blue-500/20 text-blue-200 border border-blue-400/40 text-xs px-2 py-1">National Jobs for All Network</Badge>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-serif text-white mb-2 leading-tight">
                Standing Together for Good Jobs
              </h3>
              {/* Recognition Callout */}
              <div className="inline-flex items-start gap-3 bg-gradient-to-r from-amber-500/15 to-orange-500/10 border-l-4 border-amber-400 rounded-r-lg px-4 py-3 mb-4 max-w-2xl" data-testid="njfan-recognition-callout">
                <Trophy className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-100 leading-relaxed text-left">
                  <strong className="text-amber-300">Labor Education was recognized in this NJFAN newsletter</strong> — see the shout-out to our app inside <em>"Standing Together for Good Jobs."</em>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <a
                  href="https://mailchi.mp/njfac.org/standing-together-for-good-jobs?e=4796746625"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-sm px-5 py-2.5 rounded-full shadow-lg transition-colors"
                  data-testid="njfan-newsletter-cta"
                >
                  Read the Newsletter <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/learn"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium text-sm px-5 py-2.5 rounded-full border border-white/20 transition-colors"
                  data-testid="njfan-learn-more-link"
                >
                  Learn More on the Hub <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Financial Literacy lives in the Worker Tools grid below */}



      {/* Game Banner */}
      {/* Features Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          


          {/* Free eBook Section */}
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow group overflow-hidden" data-testid="feature-ebook">
            <div className="flex flex-col md:flex-row">
              <Link to="/book" className="md:w-1/3 block overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f0e853a7-1648-4709-9fc9-01ac1e1adae5/artifacts/t2kszzoa_IMG_0817.png" 
                  alt="Struggle & Solidarity Book Cover" 
                  className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="md:w-2/3 p-8">
                <Badge className="bg-green-100 text-green-700 border-green-200 mb-3">FREE eBook</Badge>
                <h3 className="text-xl font-semibold font-serif mb-1">Struggle & Solidarity</h3>
                <p className="text-sm text-slate-500 mb-4">Labor Strikes in Memphis (1968–Present) by Kevin Bradshaw</p>
                <p className="text-slate-600 mb-4">
                  From the 1968 Sanitation Strike to the Kellogg's battles and the creation of C.O.P.P.E.R. — read the story of Memphis labor told by someone who lived it. Free to read, free to share.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/book" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors" data-testid="feature-ebook-read">
                    Read Now <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="/Struggle-and-Solidarity-Kevin-Bradshaw.epub" download className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-400 text-slate-700 font-semibold px-5 py-2.5 rounded-lg transition-colors" data-testid="feature-ebook-download">
                    <Download className="w-4 h-4" /> Download eBook
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>


      {/* Worker Tools Section - NEW */}
      <section className="py-12 bg-white" data-testid="homepage-worker-tools">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold font-serif text-primary mb-2">Worker Tools & Resources</h2>
            <p className="text-slate-600 text-sm">Free interactive tools to help you understand your rights, calculate your benefits, and take action.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/union-iq" className="group" data-testid="hp-union-iq">
              <div className="relative bg-gradient-to-br from-amber-500 to-orange-700 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full">
                <Zap className="w-8 h-8 mb-3 opacity-90" />
                <h3 className="font-bold font-serif text-base mb-1">Union IQ Quiz</h3>
                <p className="text-amber-100 text-xs leading-relaxed">12 questions, 2 minutes. Get your Union IQ score, challenge coworkers, and get a personal plan to level up.</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <Link to="/labor-history-101" className="group" data-testid="hp-labor-history">
              <div className="relative bg-gradient-to-br from-red-800 to-slate-900 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full">
                <BookOpen className="w-8 h-8 mb-3 opacity-90" />
                <h3 className="font-bold font-serif text-base mb-1">Labor History 101</h3>
                <p className="text-red-100 text-xs leading-relaxed">A narrated entry-level course: what a union is, how the movement built the middle class, and why it matters. Includes a knowledge check.</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <Link to="/academy" className="group" data-testid="hp-academy">
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-950 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full ring-1 ring-amber-400/40">
                <GraduationCap className="w-8 h-8 mb-3 text-amber-400" />
                <h3 className="font-bold font-serif text-base mb-1">Labor Education Academy</h3>
                <p className="text-slate-300 text-xs leading-relaxed">Seven free courses — Labor History 101, Steward Training, Grievance Writing, Organizing, Arbitration, Negotiation &amp; Movement Building — with printable certificates.</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <Link to="/negotiation-school" className="group" data-testid="hp-negotiation-school">
              <div className="relative bg-gradient-to-br from-yellow-600 to-amber-900 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full">
                <Scale className="w-8 h-8 mb-3 opacity-90" />
                <h3 className="font-bold font-serif text-base mb-1">Negotiation School</h3>
                <p className="text-amber-100 text-xs leading-relaxed">The frameworks from $1,600 executive courses — BATNA, anchoring, interests — free with union scenarios, quizzes, and a certificate.</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <Link to="/learn/benefits-calculator" className="group" data-testid="hp-tool-benefits">
              <div className="relative bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full">
                <TrendingUp className="w-8 h-8 mb-3 opacity-90" />
                <h3 className="font-bold font-serif text-base mb-1">Benefits Calculator</h3>
                <p className="text-green-100 text-xs leading-relaxed">See how much more you could earn with a union contract</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <Link to="/learn/know-your-rights" className="group" data-testid="hp-tool-rights">
              <div className="relative bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full">
                <Shield className="w-8 h-8 mb-3 opacity-90" />
                <h3 className="font-bold font-serif text-base mb-1">Know Your Rights</h3>
                <p className="text-blue-100 text-xs leading-relaxed">Interactive toolkit covering your workplace rights</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
            <Link to="/sim" className="group" data-testid="hp-tool-sim">
              <div className="relative bg-gradient-to-br from-orange-600 to-slate-900 rounded-xl p-5 text-white hover:shadow-lg hover:scale-[1.02] transition-all h-full">
                <Building2 className="w-8 h-8 mb-3 opacity-90" />
                <h3 className="font-bold font-serif text-base mb-1">3D Construction Trades Sim</h3>
                <p className="text-orange-100 text-xs leading-relaxed">Step onto a virtual jobsite: PPE, hazard hunts, tool ID, ladder safety and more — interactive MC3 trades drills.</p>
                <ArrowRight className="w-4 h-4 absolute bottom-4 right-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          </div>
          <div className="text-center mt-8">
            <Link to="/learn" data-testid="hp-see-all-tools">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold px-8">
                <span className="mr-2 text-[11px] font-bold px-2 py-0.5 bg-white/20 rounded-full">10 FREE TOOLS</span>
                See all tools &amp; resources <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-slate-500 text-xs mt-3">Calculators, courses, rights guides, the 3D trades sim &amp; more — all free.</p>
          </div>
        </div>
      </section>

      {/* Union Jobs & Join CTA */}
      <section className="py-12 bg-red-700" data-testid="join-union-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Users className="w-9 h-9 text-white" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white font-serif">Not in a Union Yet?</h3>
                <p className="text-white/80 text-sm md:text-base">Find union jobs or contact an AFL-CIO organizer to start organizing your workplace.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/jobs">
                <Button size="lg" className="bg-white text-red-700 hover:bg-white/90 font-semibold px-8 whitespace-nowrap" data-testid="cta-union-jobs-btn">
                  Union Job Board <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="https://aflcio.org/formaunion/contact" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8 whitespace-nowrap" data-testid="cta-join-union-btn">
                  Join a Union <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>



      {/* Live Platform Stats */}
      <LiveStatsSection />

      {/* Signature quote */}
      <section className="py-14 bg-slate-50" data-testid="home-bradshaw-quote">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Crown className="w-7 h-7 text-amber-500 mx-auto mb-4" />
          <p className="font-serif text-xl md:text-2xl italic text-slate-800 leading-snug">
            &ldquo;May we build with hands strengthened by solidarity, sit at tables shaped by the dignity of our
            labor, and leave every generation of workers more power and a greater share of what they create.&rdquo;
          </p>
          <p className="mt-4 font-bold text-amber-700">— Kevin Bradshaw</p>
          <p className="text-xs text-slate-500">President, Memphis CLC · Founder of Labor Education</p>
        </div>
      </section>

    </div>
  );
};

export { LandingPage };
